// frontend/src/pages/admin/Modules.tsx
import React, { useEffect, useState } from 'react';
import { Plus, Edit, Trash, Video, Clock, Package } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

interface Product {
  id: number;
  nom: string;
}

interface Module {
  id: number;
  product_id: number;
  titre: string;
  description: string;
  ordre: number;
  video_url: string;
  duree_minutes: number;
  created_at: string;
  product: Product;
}

interface ModuleFormData {
  product_id: number;
  titre: string;
  description: string;
  ordre: number;
  video_url: string;
  duree_minutes: number;
}

export default function AdminModules() {
  const [modules, setModules] = useState<Module[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingModule, setEditingModule] = useState<Module | null>(null);
  const [formData, setFormData] = useState<ModuleFormData>({
    product_id: 0,
    titre: '',
    description: '',
    ordre: 1,
    video_url: '',
    duree_minutes: 0
  });

  useEffect(() => {
    fetchModules();
    fetchProducts();
  }, []);

  const fetchModules = async () => {
    try {
      const response = await api.get('/admin/modules');
      setModules(response.data);
    } catch {
      toast.error('Erreur lors du chargement des modules');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get('/admin/products');
      setProducts(response.data);
    } catch {
      console.error('Erreur lors du chargement des produits');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingModule) {
        await api.put(`/admin/modules/${editingModule.id}`, formData);
        toast.success('Module modifié avec succès');
      } else {
        await api.post('/admin/modules', formData);
        toast.success('Module créé avec succès');
      }
      setShowModal(false);
      setEditingModule(null);
      resetForm();
      fetchModules();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce module ?')) return;
    try {
      await api.delete(`/admin/modules/${id}`);
      toast.success('Module supprimé');
      fetchModules();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const openEditModal = (module: Module) => {
    setEditingModule(module);
    setFormData({
      product_id: module.product_id,
      titre: module.titre,
      description: module.description,
      ordre: module.ordre,
      video_url: module.video_url,
      duree_minutes: module.duree_minutes
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      product_id: products[0]?.id || 0,
      titre: '',
      description: '',
      ordre: 1,
      video_url: '',
      duree_minutes: 0
    });
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête (responsive) */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des modules</h1>
          <p className="mt-1 text-gray-600">
            {modules.length} module{modules.length > 1 ? 's' : ''} au total
          </p>
        </div>
        <button
          onClick={() => {
            setEditingModule(null);
            resetForm();
            setShowModal(true);
          }}
          className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nouveau module
        </button>
      </div>

      {/* Liste des modules (Desktop - tableau) */}
      <div className="hidden md:block bg-white shadow rounded-lg">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Titre</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Produit</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ordre</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Durée</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Créé le</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {modules.map((module) => (
                <tr key={module.id}>
                  <td className="px-6 py-4 whitespace-nowrap">{module.titre}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{module.product?.nom}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{module.ordre}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{module.duree_minutes} min</td>
                  <td className="px-6 py-4 whitespace-nowrap">{formatDate(module.created_at)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right space-x-2">
                    <button
                      onClick={() => openEditModal(module)}
                      className="text-primary hover:text-primary-dark"
                      aria-label="Modifier"
                    >
                      <Edit className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(module.id)}
                      className="text-red-600 hover:text-red-900"
                      aria-label="Supprimer"
                    >
                      <Trash className="h-5 w-5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Liste des modules (Mobile - cartes) */}
      <div className="md:hidden space-y-3">
        {modules.map((module) => (
          <div key={module.id} className="bg-white shadow rounded-lg p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900 break-words">{module.titre}</h3>
                <div className="mt-1 text-sm text-gray-600 flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center gap-1">
                    <Package className="h-4 w-4 text-gray-400" />
                    {module.product?.nom}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Clock className="h-4 w-4 text-gray-400" />
                    {module.duree_minutes} min
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Video className="h-4 w-4 text-gray-400" />
                    Ordre {module.ordre}
                  </span>
                </div>
                {module.description && (
                  <p className="text-sm text-gray-500 mt-2 break-words">{module.description}</p>
                )}
                <div className="mt-2 text-xs text-gray-500">Créé le {formatDate(module.created_at)}</div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => openEditModal(module)}
                  className="p-2 rounded-md text-primary hover:bg-blue-50"
                  aria-label="Modifier"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(module.id)}
                  className="p-2 rounded-md text-red-600 hover:bg-red-50"
                  aria-label="Supprimer"
                >
                  <Trash className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal formulaire (actions sticky compactes comme Users) */}
      {showModal && (
        <div className="modal-backdrop fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/40">
          <div className="modal-container w-full max-w-2xl bg-white rounded-xl shadow-xl p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-4">
              {editingModule ? 'Modifier le module' : 'Nouveau module'}
            </h2>

            <form onSubmit={handleSubmit}>
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Titre</label>
                  <input
                    type="text"
                    value={formData.titre}
                    onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                    required
                    className="mt-1.5 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-primary focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="mt-1.5 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-primary focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Produit</label>
                  <select
                    value={formData.product_id}
                    onChange={(e) => setFormData({ ...formData, product_id: parseInt(e.target.value) })}
                    required
                    className="mt-1.5 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-primary focus:border-primary"
                  >
                    {products.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.nom}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Ordre</label>
                    <input
                      type="number"
                      value={formData.ordre}
                      onChange={(e) =>
                        setFormData({ ...formData, ordre: parseInt(e.target.value || '0', 10) })
                      }
                      required
                      className="mt-1.5 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-primary focus:border-primary"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Durée (min)</label>
                    <input
                      type="number"
                      value={formData.duree_minutes}
                      onChange={(e) =>
                        setFormData({ ...formData, duree_minutes: parseInt(e.target.value || '0', 10) })
                      }
                      required
                      className="mt-1.5 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-primary focus:border-primary"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">URL de la vidéo</label>
                  <input
                    type="url"
                    value={formData.video_url}
                    onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                    required
                    className="mt-1.5 block w-full border-gray-300 rounded-lg shadow-sm focus:ring-primary focus:border-primary"
                  />
                </div>
              </div>

              {/* Actions compactes identiques à AdminUsers */}
              <div className="mt-6 sm:mt-8 sticky bottom-0 bg-white pb-2 pt-3">
                <div className="flex flex-wrap justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingModule(null);
                      resetForm();
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark"
                  >
                    {editingModule ? 'Modifier' : 'Créer'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
